/* eslint-disable react-hooks/exhaustive-deps */

import styled from '@emotion/styled'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import { CssBaseline, Grid } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import '@total-typescript/ts-reset'
import { SessionProvider, signIn, useSession } from 'next-auth/react'
import { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { GlobalStoreProvider, useGlobalActions, useGlobalStore } from '../global-store'
import { getAppConfig } from '../src/api/app-config'
import { getContent } from '../src/api/content'
import { getEvents } from '../src/api/events'
import { getAllRequests } from '../src/api/requests'
import { getUserInfo } from '../src/api/user'
import {
  clearStoredInitIndicator,
  getStoredInitIndicator,
  setStoredInitIndicator,
} from '../src/app/auth-signer-queue/util'
import GlobalFooter from '../src/components/global-footer'
import GlobalNav from '../src/components/global-nav'
import { IDLE_TIME } from '../src/config'
import '../src/styles/main.css'
import { theme } from '../src/styles/theme-override'
import { apiKeyStorageKey, apiRootUriStorageKey, setStoredApiConfig } from '../src/utils'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { background, backgroundColor } from '../src/tokens'
import { evaluateSessionGuard } from '../src/auth/session-guard'
import { shouldAttemptSignIn } from '../src/auth/sign-in-guard'

const isAuthClientDebugEnabled = process.env.NEXT_PUBLIC_AUTH_DEBUG_LOGS === 'true'

const logAuthClientDebug = (message: string, context: Record<string, unknown> = {}) => {
  if (!isAuthClientDebugEnabled) return
  console.info('[auth-client-debug]', message, context)
}

export default function App({ Component, pageProps: { session, ...pageProps } }: AppProps) {
  const [apiConfigIsValid, setApiConfigIsValid] = useState(false)
  const [appIsOffline, setAppIsOffline] = useState(false)
  const router = useRouter()

  const onIdle = () => {
    if (typeof window !== undefined) {
      router.replace('/session')
    }
  }

  const queryClient = new QueryClient()

  useIdleTimer({ onIdle, timeout: IDLE_TIME })

  useEffect(() => {
    getAppConfig().then((results) => {
      if (results && results.apiRootUri && results.apiKey) {
        setStoredApiConfig({
          [apiRootUriStorageKey]: results.apiRootUri,
          [apiKeyStorageKey]: results.apiKey,
        })

        if (results.appOffline && results.appOffline !== 'false') {
          setAppIsOffline(true)
        }
        setApiConfigIsValid(true)
      }
    })
  }, [])

  useEffect(() => {
    if (appIsOffline) {
      router.replace('/offline')
    }
  }, [appIsOffline])

  return apiConfigIsValid ? (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools />
        <GlobalStoreProvider>
          <div
            id="app-bg"
            style={{
              background: !!router.pathname.includes('admin') ? background.admin : background.default,
              backgroundColor: !!router.pathname.includes('admin')
                ? backgroundColor.admin
                : backgroundColor.default,
            }}
          />
          <div style={{ position: 'relative' }}>
            {router.pathname === '/session' ||
            router.pathname === '/offline' ||
            router.pathname === '/auth/error' ? (
              <Component {...pageProps} />
            ) : appIsOffline ? (
              <></>
            ) : (
              <SessionProvider session={session}>
                <SessionComponent Component={Component} pageProps={pageProps} />
              </SessionProvider>
            )}
          </div>
        </GlobalStoreProvider>
      </QueryClientProvider>
    </ThemeProvider>
  ) : (
    <></>
  )
}

function SessionComponent({ Component, pageProps }: Omit<AppProps, 'router'>) {
  const { data: session, status } = useSession()
  const router = useRouter()
  const [publicUrl, setIsPublic] = useState(false)
  const authRedirectInFlight = useRef(false)

  // If the server-side token refresh failed, force a new Azure sign-in so the
  // user gets a fresh session rather than hitting 401s on every API call.
  // The loop guard keeps us from bouncing the user to Azure repeatedly when
  // the refreshed session also comes back broken (e.g. backend misconfig).
  useEffect(() => {
    if (session?.error !== 'RefreshAccessTokenError') return
    if (!shouldAttemptSignIn()) {
      logAuthClientDebug(
        'Access token refresh failed but sign-in loop guard tripped; routing to /auth/error',
        { path: router.pathname }
      )
      // Expired session: route to the recovery page instead of "Access denied",
      // forwarding the current path so re-auth returns the user here.
      router.replace(`/auth/error?error=SessionExpired&from=${encodeURIComponent(router.asPath)}`)
      return
    }
    logAuthClientDebug('Access token refresh failed; re-initiating sign-in', {
      path: router.pathname,
    })
    signIn('azure-ad', { callbackUrl: router.asPath })
  }, [session?.error, router.pathname])

  useEffect(() => {
    // The refresh-error effect above owns sign-in handling when the server-side
    // token refresh has failed. Bail out here so we don't race it with our own
    // signIn() — the loop guard would catch the duplicate, but the survivor
    // would be whichever fired first, and we'd silently lose the callbackUrl
    // from the refresh-error effect's call.
    if (session?.error === 'RefreshAccessTokenError') return

    const isGuestRoute = router.pathname.includes('/guest-services')
    const initIndicator = !!getStoredInitIndicator()
    const decision = evaluateSessionGuard({
      status,
      isGuestRoute,
      isPublicUrl: publicUrl,
      hasInitIndicator: initIndicator,
      redirectInFlight: authRedirectInFlight.current,
    })

    if (typeof decision.setPublicUrl === 'boolean') {
      setIsPublic(decision.setPublicUrl)
    }

    if (typeof decision.setRedirectInFlight === 'boolean') {
      authRedirectInFlight.current = decision.setRedirectInFlight
    }

    if (decision.clearInitIndicator) {
      clearStoredInitIndicator()
    }

    if (decision.setInitIndicator) {
      setStoredInitIndicator()
    }

    if (decision.action === 'guest-route') {
      logAuthClientDebug('Skipping auth for guest route', {
        path: router.pathname,
      })
      return
    }

    if (decision.action === 'wait-for-status') {
      logAuthClientDebug('Waiting for session status', {
        status,
        path: router.pathname,
      })
      return
    }

    if (decision.action === 'authenticated') {
      logAuthClientDebug('Session authenticated', {
        status,
        path: router.pathname,
      })
      return
    }

    if (decision.action === 'wait-for-in-flight-sign-in') {
      logAuthClientDebug('Waiting for in-flight Azure sign-in redirect', {
        path: router.pathname,
      })
      return
    }

    if (decision.action === 'redirect-to-session') {
      logAuthClientDebug('Auth already initiated, redirecting to session page', {
        path: router.pathname,
        initIndicator,
        redirectInFlight: authRedirectInFlight.current,
      })
      router.replace('/session')
      return
    }

    if (decision.action === 'start-sign-in') {
      logAuthClientDebug('Starting Azure sign-in redirect', {
        path: router.pathname,
      })
      signIn('azure-ad')
    }
  }, [publicUrl, router, status, session?.error])

  return session || publicUrl ? (
    <DataComponent Component={Component} pageProps={pageProps} session={session} isPublic={publicUrl} />
  ) : (
    <></>
  )
}

interface DataProps extends Omit<AppProps, 'router'> {
  session: any
  isPublic: boolean
}

function DataComponent({ Component, pageProps, session, isPublic }: DataProps) {
  const [isAuth, setIsAuth] = useState<boolean>(false)

  const { currentEvents, requestedEvents, content, userInfo } = useGlobalStore()

  const { setCurrentEvents, setRequestedEvents, setContent, setUserInfo } = useGlobalActions()

  const router = useRouter()

  useEffect(() => {
    if (!userInfo.data && !userInfo.isLoading && session?.user?.email && !isPublic) {
      setUserInfo({ isLoading: true })
      getUserInfo()
        .then((data: any) => {
          if (
            !data ||
            !data.EmployeeDataModel ||
            !data.EmployeeDataModel.RequestTixAvaliability ||
            data.EmployeeDataModel.EmployeeStatus === 'I'
          ) {
            setUserInfo({
              isAuth: true,
              data: [],
              isLoading: false,
            })
            router.replace('/unauth')
          } else {
            setIsAuth(true)
            setUserInfo({
              isAuth: true,
              data: data,
              isLoading: false,
              isError: false,
            })
          }
        })
        .catch((error: any) => {
          setUserInfo({ isError: true, data: {}, isLoading: false, isAuth: isAuth })
          // 401 / token-refresh failures = expired session → recovery page;
          // reserve /unauth for genuine non-auth failures.
          const isAuthError =
            error?.statusCode === 401 ||
            (typeof error?.message === 'string' && error.message.includes('Token refresh failed'))
          router.replace(
            isAuthError
              ? `/auth/error?error=SessionExpired&from=${encodeURIComponent(router.asPath)}`
              : '/unauth'
          )
        })
    } else if (
      isPublic &&
      session === null &&
      !userInfo.data &&
      !userInfo.isLoading &&
      !session?.user?.email
    ) {
      setIsAuth(true)
      setUserInfo({
        isAuth: true,
        data: [],
        isLoading: false,
      })
    }
  }, [userInfo, session])

  useEffect(() => {
    if (userInfo?.isAuth && !content.data && !content.isLoading && !isPublic) {
      setContent({ isLoading: true })
      getContent()
        .then((data) => {
          setContent({
            data: data?.length ? data : [],
            isLoading: false,
            isError: false,
          })
        })
        .catch(() => {
          setContent({ isError: true, data: [], isLoading: false })
        })
    }
  }, [userInfo?.isAuth])

  // to reduce the heavy lifting by consuming components, maybe refactor to have api functions handle all error and loading states
  useEffect(() => {
    if (userInfo?.isAuth && !currentEvents?.data && !isPublic) {
      setCurrentEvents({ isLoading: true })
      getEvents()
        .then((data) => {
          setCurrentEvents({
            data: data?.EventsByRequest,
            isLoading: false,
            totalCount: data?.TotalCount,
            isError: false,
          })
        })
        .catch(() => {
          setCurrentEvents({ isError: true, data: [], isLoading: false, totalCount: 0 })
        })
    }
  }, [userInfo?.isAuth, currentEvents?.data])

  useEffect(() => {
    if (
      userInfo?.isAuth &&
      !requestedEvents.data &&
      !requestedEvents.isError &&
      !requestedEvents.isLoading &&
      !userInfo.isLoading &&
      userInfo.data &&
      !isPublic
    ) {
      setRequestedEvents({ isLoading: true })
      getAllRequests({ employeeId: userInfo?.data?.EmployeeDataModel?.EmployeeId ?? '' })
        .then((data) => {
          setRequestedEvents({
            data: data?.SubmittedRequests,
            isLoading: false,
            totalCount: data?.TotalCount,
            isError: false,
          })
        })
        .catch(() => {
          setRequestedEvents({ isError: true, data: [], isLoading: false, totalCount: 0 })
        })
    }
  }, [userInfo, requestedEvents, userInfo?.isAuth])

  return (
    <GlobalWrapper>
      <GlobalNav show={!router.query.fullscreen && !router.pathname.includes('guest-services')} />
      <Grid
        container
        sx={{
          margin: '0 auto',
          flexGrow: 1,
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <Component {...pageProps} />
      </Grid>
      <GlobalFooter show={!router.query.fullscreen} />
    </GlobalWrapper>
  )
}

const GlobalWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`
